import { mdiDotsHorizontal, mdiFastForwardOutline, mdiPlayOutline } from '@mdi/js'
import React, { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ButtonDropdown } from 'sfportal_components_forms/ButtonDropdown'
import { IListGroupTaskUtils, Props } from 'sfportal_components_listitem_utils_interfaces/IListGroupTaskUtils'
import { MenuItem } from 'sfportal_components_menu/MenuItem'
import { MenuTitle } from 'sfportal_components_menu/MenuTitle'
import { RequestStatus } from 'sfportal_services_api/generic/types'
import { claimTasks, loadFormfieldsForTask, useProductDetailStore } from 'sfportal_stores/productDetailStore'
import { useUserStore } from 'sfportal_stores/userStore'
import { noop } from 'sfportal_utils/function'
import { ApiTask, instanceOfApiTaskDataResourceAsset } from '../../../../services/api/apiSchemas'
import { useCompleteMultipleTasksModal } from '../../hooks/useCompleteMultipleTaskModal'

/**
 * DropDown für den Header von Gruppen.
 */
const GroupHeaderDropDown: FC<Props> = function GroupHeaderDropDown ({
  tasks,
  title
}) {
  const { currentUser } = useUserStore()
  const { taskActionStatus } = useProductDetailStore()
  const { t } = useTranslation()

  /**
   * Array mit allen Tasks, die bereits dem User zugeordnet sind.
   */
  const claimedTasks = useMemo(() => {
    if (currentUser === null) {
      return []
    }
    return tasks.filter((task) => task.assignee === currentUser.username)
  }, [currentUser, tasks])

  const {
    completeMultipleTasksModal,
    setCompleteMultipleTasksModalVisible
  } =
    useCompleteMultipleTasksModal({
      tasks: claimedTasks
    })

  /**
   * Array mit allen Tasks, die der User übernehmen kann.
   */
  const claimableTasks = useMemo(() => {
    return tasks.filter(
      (task) => !task.assignee && task.taskPrivileges.canUserClaim
    )
  }, [tasks])

  /**
   * Gibt an ob der aktuell angemeldete User Tasks übernehmen kann.
   */
  const currentUserCanClaimTasks = useMemo(
    () => claimableTasks.length > 0,
    [claimableTasks.length]
  )

  /**
   * Übernimmt alle Tasks, die dem angemeldeten User zugeordnet werden können.
   */
  function handleClaimAllTasks (): void {
    const taskIds = claimableTasks.map((task) => task.id)
    claimTasks(...taskIds).catch(noop)
  }

  /**
   * Schließt alle dem User zurgeordneten Tasks ab.
   */
  async function handleCompleteAllTasks (): Promise<void> {
    if (claimedTasks.filter((claimedTask: ApiTask) => claimedTask.formFields !== undefined).length === 0) {
      // Keines der Tasks hat die formfelder geladen. Da allerdings jeder task der gleiche "tasktype" ist,
      // reicht es den ersten zu laden. Diese Tasks werden dann verwendet.
      await loadFormfieldsForTask(claimedTasks[0].id)
    }
    setCompleteMultipleTasksModalVisible(true)
  }

  /**
   * Boolean der angibt, ob gerade eine Action läuft, wordurch die Taskbuttons in der Ui gesperrt werden.
   */
  const taskActionIsPending = useMemo(
    () => taskActionStatus === RequestStatus.pending,
    [taskActionStatus]
  )

  /**
   * Gibt an ob die Assets aller Tasks entsperrt sind
   */
  const tasksUnlocked = useMemo(
    () =>
      tasks.filter((task: ApiTask) => {
        if (instanceOfApiTaskDataResourceAsset(task.dataResource)) {
          return task.dataResource.locked
        }

        return false
      }).length === 0,
    [tasks]
  )

  /**
   * Gibt an ob dem aktuellen User Tasks zugeodnet sind.
   */
  const currentUserHasTasksAssigned = useMemo(
    () => claimedTasks.length > 0,
    [claimedTasks.length]
  )

  if (claimableTasks.length <= 0 && claimedTasks.length <= 0) {
    return null
  }

  return (
    <>
      {completeMultipleTasksModal}

      <ButtonDropdown
        buttonStyle="inline"
        buttonIcon={mdiDotsHorizontal}
        title={t('listItemGroupTask.moreActions')}
      >
        <MenuTitle
          label={t('listItemGroupTask.menuTitle', {
            count: tasks.length,
            title
          })}
        />

        {currentUserCanClaimTasks ? (
          <MenuItem
            label={t('listItemGroupTask.claimAllTasks')}
            icon={mdiPlayOutline}
            action={handleClaimAllTasks}
            enabled={!taskActionIsPending}
          />
        ) : null}

        {currentUserHasTasksAssigned ? (
          <MenuItem
            label={t('listItemGroupTask.completeAllTasks')}
            icon={mdiFastForwardOutline}
            action={handleCompleteAllTasks}
            enabled={!taskActionIsPending && tasksUnlocked}
          />
        ) : null}
      </ButtonDropdown>
    </>
  )
}

/**
 * Utils mit Funktionen und Komponenten zu Task Gruppen.
 */
export const ListItemGroupTaskUtils: IListGroupTaskUtils = {
  /**
   * DropDown für den Header von Gruppen.
   */
  GroupHeaderDropdown: GroupHeaderDropDown
}
